import style from "./UserProfile.module.css";

import { useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import ProfileBanner from "@/features/auth/components/profileBanner/ProfileBanner";
import ProfileConent from "@/features/auth/components/profileContent/ProfileContent";

import { useGetUserByUsername } from "@/features/auth/hooks/useGetUserByUsername";
import { useGetUserCertificates } from "@/features/certificates/hooks/useGetUserCertificates";
import { buildProfileSeo, courseMetaTags } from "@/utils/seo";
import FullPageLoader from "@/ui/fullPageLoader/FullPageLoader";
import { useCurrentLanguage } from "@/i18n/useLocalized";

// Mirrors the tags scripts/prerender/render-head.mjs writes for a profile, so
// react-helmet-async adopts them instead of appending a second copy once
// profiles are prerendered. Both sides derive from buildProfileSeo — see
// src/utils/seo.js.
function ProfileSeo({ username, user, certificateCount }) {
	const language = useCurrentLanguage();
	const seo = buildProfileSeo({
		username,
		firstName: user?.first_name,
		lastName: user?.last_name,
		certificateCount,
		language,
	});

	return (
		<Helmet>
			<title>{seo.title}</title>
			{courseMetaTags({ ...seo, language }).map((tag) => (
				<meta key={tag.name ?? tag.property} {...tag} />
			))}
			<link rel="canonical" href={seo.canonical} />
		</Helmet>
	);
}

function UserProfile() {
	const { username } = useParams();
	const { user, isPending, isError } = useGetUserByUsername(username);
	// Shares react-query's cache key with the certificates tab, so asking for
	// the count here costs no extra request. The description states the count,
	// so the tags are withheld until it is known rather than being rendered
	// once with a wrong number and then corrected.
	const { certificates, isPending: isCertificatesPending } =
		useGetUserCertificates(username);

	if (isPending) return <FullPageLoader />;
	if (isError) return <Navigate to="/404" />;

	return (
		<>
			{!isCertificatesPending && (
				<ProfileSeo
					username={username}
					user={user}
					certificateCount={certificates.length}
				/>
			)}
			<ProfileBanner username={username} />
			<ProfileConent username={username} />
		</>
	);
}

export default UserProfile;
